import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Search,
  ClipboardList,
  CheckCircle,
  PhoneIncoming,
  Clock,
  ChevronDown,
  ChevronRight,
  Mic,
  ExternalLink,
  ChevronLeft,
  Calendar,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, LabelList } from 'recharts';
import { supabase } from '../../services/adminService';

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
};

const last10 = (v) => {
  const d = String(v ?? '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
};

const fmtDuration = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n) || n <= 0) return '0s';
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// Sarv recordings are stored as relative `file` paths; we need to construct the
// full streaming URL using the per-account `sarv_user_id`. Default account id
// `68798380` is used as a safe fallback for the WFH Sarv account.
const SARV_RECORDING_BASE = 'https://s-ct3.sarv.com/v2/recording/direct';
const SARV_DEFAULT_USER_ID = '68798380';

const buildSarvRecordingUrl = (file, sarvUserId) => {
  if (!file || typeof file !== 'string') return null;
  const path = file.trim();
  if (!path) return null;
  const userId = String(sarvUserId || SARV_DEFAULT_USER_ID).trim() || SARV_DEFAULT_USER_ID;
  const sep = path.startsWith('/') ? '' : '/';
  return `${SARV_RECORDING_BASE}/${userId}${sep}${path}`;
};

// Extract recording URLs from a call_report row, handling both Sarv and SmartFlo (Tata) shapes.
const extractRecordingUrls = (call) => {
  const out = [];
  const seen = new Set();
  const push = (url, label) => {
    if (!url || typeof url !== 'string') return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    if (!/^https?:\/\//i.test(trimmed)) return;
    seen.add(trimmed);
    out.push({ url: trimmed, label: label || `Recording ${out.length + 1}` });
  };

  const full = call?.full_row || {};
  const sarvUserId = full.sarv_user_id;

  // SmartFlo / Tata: dedicated column
  if (full.sf_recording_url) push(full.sf_recording_url, full.sf_recording_name || 'Tata recording');

  // Common: jsonb `recordings`
  const recs = call?.recordings ?? full.recordings;
  if (Array.isArray(recs)) {
    recs.forEach((r, i) => {
      if (typeof r === 'string') {
        // Could be a full URL or a Sarv relative path
        if (/^https?:\/\//i.test(r)) push(r, `Recording ${i + 1}`);
        else push(buildSarvRecordingUrl(r, sarvUserId), `Sarv recording ${i + 1}`);
      } else if (r && typeof r === 'object') {
        const directUrl = r.url || r.recording || r.recording_url || r.location;
        if (directUrl && /^https?:\/\//i.test(String(directUrl))) {
          push(directUrl, r.name || r.label);
        } else if (r.file || r.path) {
          // Sarv format: { file: "/path/to/recording.mp3", duration: ... }
          push(buildSarvRecordingUrl(r.file || r.path, sarvUserId), r.name || r.label || `Sarv recording ${i + 1}`);
        } else if (directUrl) {
          // Some Sarv responses give relative `recording` path
          push(buildSarvRecordingUrl(directUrl, sarvUserId), r.name || r.label);
        }
      }
    });
  } else if (recs && typeof recs === 'object') {
    const directUrl = recs.url || recs.recording || recs.recording_url;
    if (directUrl && /^https?:\/\//i.test(String(directUrl))) push(directUrl, recs.name);
    else if (recs.file || recs.path) push(buildSarvRecordingUrl(recs.file || recs.path, sarvUserId), recs.name);
  }

  // Fallback: raw text might itself be a URL or a JSON-encoded recordings array
  const raw = call?.recordings_raw ?? full.recordings_raw;
  if (typeof raw === 'string' && raw.trim()) {
    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      push(trimmed, 'Recording');
    } else if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        arr.forEach((r, i) => {
          if (!r) return;
          if (typeof r === 'string') {
            if (/^https?:\/\//i.test(r)) push(r, `Recording ${i + 1}`);
            else push(buildSarvRecordingUrl(r, sarvUserId), `Sarv recording ${i + 1}`);
          } else if (typeof r === 'object') {
            const u = r.url || r.recording || r.recording_url || r.location;
            if (u && /^https?:\/\//i.test(String(u))) push(u, r.name);
            else if (r.file || r.path) push(buildSarvRecordingUrl(r.file || r.path, sarvUserId), r.name || `Sarv recording ${i + 1}`);
          }
        });
      } catch {
        /* ignore */
      }
    } else if (trimmed.startsWith('/')) {
      // Bare Sarv-style relative path
      push(buildSarvRecordingUrl(trimmed, sarvUserId), 'Sarv recording');
    }
  }

  return out;
};

const detectProvider = (call) => {
  const full = call?.full_row || {};
  if (full.provider) return String(full.provider).toLowerCase();
  if (full.sf_uuid || full.sf_recording_url) return 'smartflo';
  if (full.sarv_user_id || full.sarv_camp_id) return 'sarv';
  return 'sarv';
};

const providerBadge = (p) => {
  const v = String(p || '').toLowerCase();
  if (v === 'smartflo' || v === 'tata') {
    return { label: 'Tata', cls: 'bg-orange-50 text-orange-700 border-orange-200' };
  }
  return { label: 'Sarv', cls: 'bg-purple-50 text-purple-700 border-purple-200' };
};

const callStatusBadge = (status, disposition) => {
  const s = String(status ?? disposition ?? '').toLowerCase();
  if (!s) return { label: 'Unknown', cls: 'bg-gray-100 text-gray-700' };
  if (/(answered|connected|complete|success|3$|5$|6$|10$)/.test(s)) {
    return { label: status || disposition || 'Answered', cls: 'bg-emerald-50 text-emerald-700' };
  }
  if (/(missed|no.?answer|noanswer|cancel|busy|failed|reject|7$|8$|9$|13$|15$|16$|17$|18$|19$)/.test(s)) {
    return { label: status || disposition || 'Missed', cls: 'bg-red-50 text-red-700' };
  }
  return { label: status || disposition || 'Other', cls: 'bg-gray-100 text-gray-700' };
};

const WFHTelecallerDetailReport = () => {
  const { telecallerId } = useParams();
  const [sp, setSp] = useSearchParams();

  const teamLeadId = sp.get('teamLeadId') || '';
  const batch = sp.get('batch') || '';
  const from = sp.get('from') || '';
  const to = sp.get('to') || '';
  const tcName = sp.get('name') || '';
  const tcEmail = sp.get('email') || '';

  // ---- Date helpers (YYYY-MM-DD, IST-friendly local date math) ----
  const todayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const shiftDateStr = (s, deltaDays) => {
    const base = (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) ? s : todayStr();
    const [y, m, d] = base.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + deltaDays);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };
  const shiftDays = (delta) => {
    const next = new URLSearchParams(sp);
    const curFrom = next.get('from') || todayStr();
    const curTo = next.get('to') || curFrom;
    next.set('from', shiftDateStr(curFrom, delta));
    next.set('to', shiftDateStr(curTo, delta));
    setSp(next, { replace: false });
  };
  const goToday = () => {
    const next = new URLSearchParams(sp);
    const t = todayStr();
    next.set('from', t);
    next.set('to', t);
    setSp(next, { replace: false });
  };
  const isSingleDay = !!from && !!to && from === to;
  const isToday = isSingleDay && from === todayStr();

  const [q, setQ] = useState('');
  const [claimedGranularity, setClaimedGranularity] = useState('1h');

  const GRANULARITY_OPTIONS = [
    { key: '1d', label: '1d', bucketMinutes: 1440 },
    { key: '8h', label: '8h', bucketMinutes: 480 },
    { key: '4h', label: '4h', bucketMinutes: 240 },
    { key: '2h', label: '2h', bucketMinutes: 120 },
    { key: '1h', label: '1h', bucketMinutes: 60 },
    { key: '30min', label: '30m', bucketMinutes: 30 },
    { key: '15min', label: '15m', bucketMinutes: 15 },
    { key: '5min', label: '5m', bucketMinutes: 5 },
  ];

  const { data: rows = [], isLoading, error, refetch } = useQuery({
    queryKey: ['wfhTelecallerDetail', telecallerId, teamLeadId, batch, from, to],
    enabled: !!telecallerId && !!teamLeadId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_wfh_telecaller_activity', {
        p_team_lead_id: teamLeadId,
        p_telecaller_id: telecallerId,
        p_batch_name: batch || null,
        p_from_date: from || null,
        p_to_date: to || null,
      });
      if (error) throw error;
      return data || [];
    },
  });

  // Build a unique list of 10-digit phone numbers from leads (contact + alternate)
  const phoneList = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => {
      const a = last10(r?.contact_number);
      const b = last10(r?.alternate_number);
      if (a && a.length === 10) set.add(a);
      if (b && b.length === 10) set.add(b);
    });
    return Array.from(set);
  }, [rows]);

  // Fetch all matching call reports (Sarv + SmartFlo/Tata) in one shot
  const { data: callsByPhone = {}, isFetching: isFetchingCalls } = useQuery({
    queryKey: ['wfhCallsForPhones', telecallerId, phoneList.join(','), from, to],
    enabled: phoneList.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_wfh_calls_for_phones', {
        p_phones: phoneList,
        p_from_date: from || null,
        p_to_date: to || null,
      });
      if (error) {
        console.warn('admin_wfh_calls_for_phones failed:', error);
        return {};
      }
      const map = {};
      (data || []).forEach((c) => {
        const key = String(c.customer_number10 || '').trim();
        if (!key) return;
        if (!map[key]) map[key] = [];
        map[key].push(c);
      });
      return map;
    },
  });

  const callsForLead = (lead) => {
    const a = last10(lead?.contact_number);
    const b = last10(lead?.alternate_number);
    const merged = [];
    const seen = new Set();
    [a, b].forEach((p) => {
      if (!p || p.length !== 10) return;
      (callsByPhone[p] || []).forEach((c) => {
        if (seen.has(c.id)) return;
        seen.add(c.id);
        merged.push(c);
      });
    });
    merged.sort((x, y) => {
      const xt = new Date(x.last_hangup_time || x.first_answer_time || x.received_at || 0).getTime();
      const yt = new Date(y.last_hangup_time || y.first_answer_time || y.received_at || 0).getTime();
      return yt - xt;
    });
    return merged;
  };

  const [expandedRowId, setExpandedRowId] = useState(null);

  // Filters: recording presence, lead status, lead disposition
  const [recordingFilter, setRecordingFilter] = useState('all'); // all | with | without | with-recording | answered | missed
  const [statusFilter, setStatusFilter] = useState('all');
  const [dispositionFilter, setDispositionFilter] = useState('all'); // all | none | <value>

  const statusOptions = useMemo(() => {
    const set = new Map(); // value -> count
    (rows || []).forEach((r) => {
      const v = String(r?.status ?? '').trim();
      if (!v) return;
      set.set(v, (set.get(v) || 0) + 1);
    });
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const dispositionOptions = useMemo(() => {
    const set = new Map();
    let noneCount = 0;
    (rows || []).forEach((r) => {
      const v = String(r?.disposition ?? '').trim();
      if (!v) {
        noneCount += 1;
        return;
      }
      set.set(v, (set.get(v) || 0) + 1);
    });
    const list = Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
    return { list, noneCount };
  }, [rows]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      // Status filter
      if (statusFilter !== 'all') {
        const v = String(r?.status ?? '').trim();
        if (v !== statusFilter) return false;
      }

      // Disposition filter
      if (dispositionFilter !== 'all') {
        const v = String(r?.disposition ?? '').trim();
        if (dispositionFilter === '__none__') {
          if (v) return false;
        } else if (v !== dispositionFilter) {
          return false;
        }
      }

      // Recording / Calls filter
      if (recordingFilter !== 'all') {
        const calls = callsForLead(r);
        if (recordingFilter === 'no-calls') {
          if (calls.length > 0) return false;
        } else if (recordingFilter === 'with-calls') {
          if (calls.length === 0) return false;
        } else if (recordingFilter === 'with-recording') {
          const hasRec = calls.some((c) => extractRecordingUrls(c).length > 0);
          if (!hasRec) return false;
        } else if (recordingFilter === 'no-recording') {
          if (calls.length === 0) return false;
          const hasRec = calls.some((c) => extractRecordingUrls(c).length > 0);
          if (hasRec) return false;
        } else if (recordingFilter === 'answered') {
          const ok = calls.some((c) => {
            const s = String(c.call_status ?? c.call_disposition ?? '').toLowerCase();
            return /(answered|connected|complete|success|^3$|^5$|^6$|^10$)/.test(s);
          });
          if (!ok) return false;
        } else if (recordingFilter === 'missed') {
          if (calls.length === 0) return false;
          const allMissed = calls.every((c) => {
            const s = String(c.call_status ?? c.call_disposition ?? '').toLowerCase();
            return /(missed|no.?answer|noanswer|cancel|busy|failed|reject|^7$|^8$|^9$)/.test(s);
          });
          if (!allMissed) return false;
        } else if (recordingFilter === 'talk-over-40') {
          if (calls.length === 0) return false;
          const ok = calls.some((c) => {
            const t = Number(c.talk_duration_seconds) || Number(c.agent_on_call_duration_seconds) || 0;
            return t > 40;
          });
          if (!ok) return false;
        }
      }

      // Search query
      if (qq) {
        const hay = [
          r.name,
          r.contact_number,
          r.alternate_number,
          r.car_number,
          r.car_model,
          r.state,
          r.pin_code,
          r.status,
          r.disposition,
          r.disposition_notes,
          r.batch_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(qq)) return false;
      }

      return true;
    });
  }, [rows, q, statusFilter, dispositionFilter, recordingFilter, callsByPhone]);

  const activeFilterCount =
    (statusFilter !== 'all' ? 1 : 0) +
    (dispositionFilter !== 'all' ? 1 : 0) +
    (recordingFilter !== 'all' ? 1 : 0);

  const callDurationStats = useMemo(() => {
    const seen = new Set();
    let totalSeconds = 0;
    let totalCalls = 0;
    let over40 = 0;
    Object.values(callsByPhone || {}).forEach((arr) => {
      (arr || []).forEach((c) => {
        if (!c || seen.has(c.id)) return;
        seen.add(c.id);
        totalCalls += 1;
        const t = Number(c.talk_duration_seconds) || Number(c.agent_on_call_duration_seconds) || 0;
        if (t > 0) totalSeconds += t;
        if (t > 40) over40 += 1;
      });
    });
    return { totalSeconds, totalCalls, over40 };
  }, [callsByPhone]);

  const totalTalkSeconds = callDurationStats.totalSeconds;
  const callsOver40 = callDurationStats.over40;
  const totalConnectedCalls = callDurationStats.totalCalls;

  const fmtHHMMSS = (secs) => {
    const n = Math.max(0, Math.floor(Number(secs) || 0));
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = n % 60;
    const pad = (x) => String(x).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  const summary = useMemo(() => {
    const out = { total: 0, disposed: 0, callback: 0, noDisp: 0, confirmed: 0 };
    (rows || []).forEach((r) => {
      out.total += 1;
      if (r.disposition) {
        out.disposed += 1;
        const d = String(r.disposition || '').trim().toLowerCase();
        if (d === 'callback') out.callback += 1;
        if (['confirmed', 'tl_confirmed', 'tl confirmed', 'tl-confirmed'].includes(d)) out.confirmed += 1;
      } else {
        out.noDisp += 1;
      }
    });
    return out;
  }, [rows]);

  const claimedByDateChart = useMemo(() => {
    const gran = GRANULARITY_OPTIONS.find((g) => g.key === claimedGranularity);
    const bucketMin = gran ? gran.bucketMinutes : 60;
    const buckets = new Map();

    (rows || []).forEach((r) => {
      if (!r.claimed_at) return;
      const d = new Date(r.claimed_at);
      if (Number.isNaN(d.getTime())) return;

      let key;
      if (bucketMin >= 1440) {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else {
        const totalMin = d.getHours() * 60 + d.getMinutes();
        const slotStart = Math.floor(totalMin / bucketMin) * bucketMin;
        const hh = String(Math.floor(slotStart / 60)).padStart(2, '0');
        const mm = String(slotStart % 60).padStart(2, '0');
        key = `${hh}:${mm}`;
      }
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });

    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([slot, count]) => ({
        date: bucketMin >= 1440 ? slot.slice(5) : slot,
        count,
      }));
  }, [rows, claimedGranularity]);

  const dispositionPieData = useMemo(() => {
    const buckets = new Map();
    (rows || []).forEach((r) => {
      const d = r.disposition ? String(r.disposition).trim() : 'No Disposition';
      buckets.set(d, (buckets.get(d) || 0) + 1);
    });
    return Array.from(buckets.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  }, [rows]);

  const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

  const CustomPieLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-3">
      {(payload || []).map((entry, i) => {
        const item = dispositionPieData[i];
        if (!item) return null;
        const pct = rows.length > 0 ? Math.round((item.value / rows.length) * 100) : 0;
        return (
          <div key={entry.value} className="flex items-center gap-1.5 text-xs text-gray-700">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
            <span className="font-medium">{item.name}</span>
            <span className="text-gray-400">({item.value} · {pct}%)</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to={`/wfh-employee-report?${new URLSearchParams({
                ...(teamLeadId ? { tl: teamLeadId } : {}),
                ...(batch ? { batch } : {}),
                ...(from ? { from } : {}),
                ...(to ? { to } : {}),
              }).toString()}`}
              className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <ArrowLeft size={16} />
              Back to report
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">WFH Telecaller Detail</h1>
          <p className="text-gray-600 mt-1">
            <span className="font-medium text-gray-900">{tcName ? tcName : fmt(telecallerId)}</span>
            {tcEmail ? <span className="text-gray-500"> · {tcEmail}</span> : null}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Filters: TeamLead {teamLeadId}
            {batch ? ` · Batch ${batch}` : ' · All batches'}
            {from || to ? ` · Date ${from || '—'} → ${to || '—'}` : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => shiftDays(-1)}
              className="px-3 py-2 hover:bg-gray-50 text-gray-700 inline-flex items-center gap-1"
              title="Previous day"
            >
              <ChevronLeft size={16} />
              <span className="hidden sm:inline text-sm">Prev day</span>
            </button>
            <div className="px-3 py-2 border-l border-r border-gray-200 bg-gray-50 inline-flex items-center gap-2 text-sm text-gray-700 min-w-[150px] justify-center">
              <Calendar size={14} className="text-gray-500" />
              {isSingleDay
                ? <span className="font-medium">{from}{isToday ? ' (Today)' : ''}</span>
                : (from || to)
                  ? <span>{from || '—'} → {to || '—'}</span>
                  : <span className="text-gray-500">No date</span>}
            </div>
            <button
              type="button"
              onClick={() => shiftDays(1)}
              className="px-3 py-2 hover:bg-gray-50 text-gray-700 inline-flex items-center gap-1"
              title="Next day"
            >
              <span className="hidden sm:inline text-sm">Next day</span>
              <ChevronRight size={16} />
            </button>
          </div>
          {!isToday && (
            <button
              type="button"
              onClick={goToday}
              className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm text-gray-700"
              title="Jump to today"
            >
              Today
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            disabled={isLoading}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Total Leads</span>
            <ClipboardList size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.total}</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Disposed</span>
            <CheckCircle size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.disposed}</div>
          {summary.total > 0 && (
            <div className="mt-1 text-xs opacity-80">{Math.round((summary.disposed / summary.total) * 100)}% of total</div>
          )}
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Confirmed</span>
            <CheckCircle size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.confirmed}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Callback</span>
            <PhoneIncoming size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.callback}</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">No Disposition</span>
            <Clock size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold">{summary.noDisp}</div>
          {summary.total > 0 && (
            <div className="mt-1 text-xs opacity-80">{Math.round((summary.noDisp / summary.total) * 100)}% pending</div>
          )}
        </div>
        <div className="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Talk Time</span>
            <Mic size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold tabular-nums">{fmtHHMMSS(totalTalkSeconds)}</div>
          <div className="mt-1 text-xs opacity-80">
            {isFetchingCalls
              ? 'Loading calls...'
              : totalTalkSeconds > 0
                ? `${Math.round(totalTalkSeconds / 60)} min total · Sarv + Tata`
                : 'No talk time yet'}
          </div>
        </div>
        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">Calls &gt; 40s</span>
            <PhoneIncoming size={18} className="opacity-70" />
          </div>
          <div className="text-3xl font-bold tabular-nums">{callsOver40}</div>
          <div className="mt-1 text-xs opacity-80">
            {isFetchingCalls
              ? 'Loading calls...'
              : totalConnectedCalls > 0
                ? `${Math.round((callsOver40 / totalConnectedCalls) * 100)}% of ${totalConnectedCalls} calls`
                : 'No calls yet'}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Claimed Leads</h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {GRANULARITY_OPTIONS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setClaimedGranularity(g.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  claimedGranularity === g.key
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="text-xs text-gray-500 mb-3">
          {rows.length} leads · selected date range
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">No leads in selected date range.</div>
        ) : claimedByDateChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={claimedByDateChart}
              margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
              barCategoryGap={claimedByDateChart.length <= 3 ? '40%' : '20%'}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '13px' }}
                formatter={(value) => [`${value} leads`, 'Claimed']}
                cursor={{ fill: 'rgba(99,102,241,0.06)' }}
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={60} fill="#6366f1">
                <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }} />
                {claimedByDateChart.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm text-gray-400 text-center py-8">No claimed_at timestamps available.</div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Disposition Breakdown</h3>
          <span className="text-xs text-gray-500">{rows.length} leads · selected date range</span>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">No data in selected date range.</div>
        ) : dispositionPieData.length > 0 ? (
          <div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dispositionPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={55}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {dispositionPieData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '13px' }}
                  formatter={(value, name) => [`${value} leads`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <CustomPieLegend payload={dispositionPieData.map((d, i) => ({ value: d.name, color: CHART_COLORS[i % CHART_COLORS.length] }))} />
          </div>
        ) : (
          <div className="text-sm text-gray-400 text-center py-8">No dispositions yet.</div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-end gap-3 flex-1">
            <div className="relative w-full sm:w-72">
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                Search
              </label>
              <Search size={16} className="absolute left-3 top-[34px] text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name / phone / car / disposition..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                Recording / Call
              </label>
              <select
                value={recordingFilter}
                onChange={(e) => setRecordingFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="all">All leads</option>
                <option value="with-calls">Calls made</option>
                <option value="no-calls">No calls</option>
                <option value="with-recording">With recording</option>
                <option value="no-recording">Called but no recording</option>
                <option value="answered">Any answered call</option>
                <option value="missed">Only missed calls</option>
                <option value="talk-over-40">Talk &gt; 40 sec</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="all">All statuses ({rows.length})</option>
                {statusOptions.map(([v, c]) => (
                  <option key={v} value={v}>
                    {v} ({c})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                Disposition
              </label>
              <select
                value={dispositionFilter}
                onChange={(e) => setDispositionFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="all">All dispositions</option>
                {dispositionOptions.noneCount > 0 && (
                  <option value="__none__">No disposition ({dispositionOptions.noneCount})</option>
                )}
                {dispositionOptions.list.map(([v, c]) => (
                  <option key={v} value={v}>
                    {v} ({c})
                  </option>
                ))}
              </select>
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('all');
                  setDispositionFilter('all');
                  setRecordingFilter('all');
                  setQ('');
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 underline"
                title="Clear filters"
              >
                Clear ({activeFilterCount})
              </button>
            )}
          </div>

          <div className="text-sm text-gray-600 whitespace-nowrap">
            <span className="font-semibold text-gray-900">{filtered.length}</span> of {rows.length} rows
          </div>
        </div>

        {!teamLeadId ? (
          <div className="text-gray-600">Missing Team Lead filter. Please open this page from WFH Employee Report.</div>
        ) : isLoading ? (
          <div className="text-gray-600">Loading...</div>
        ) : error ? (
          <div className="text-red-600">
            Failed to load detail report: {String(error?.message || error)}
            <div className="text-xs text-gray-500 mt-2">
              If you just added this feature, ensure SQL function <code>admin_wfh_telecaller_activity</code> is deployed.
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-600">No data found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disposition</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calls / Recording
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claimed At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disposition At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((r) => {
                  const calls = callsForLead(r);
                  const callCount = calls.length;
                  const recCount = calls.reduce((acc, c) => acc + extractRecordingUrls(c).length, 0);
                  const isExpanded = expandedRowId === r.id;
                  const canExpand = callCount > 0;
                  return (
                    <React.Fragment key={String(r.id)}>
                      <tr
                        className={`hover:bg-gray-50 ${canExpand ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (canExpand) setExpandedRowId(isExpanded ? null : r.id);
                        }}
                      >
                        <td className="px-4 py-3 align-top text-gray-400">
                          {canExpand ? (
                            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                          ) : null}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-gray-900">{fmt(r.name)}</div>
                          <div className="text-xs text-gray-500">
                            {fmt(r.car_model)} {r.car_number ? `· ${r.car_number}` : ''} {r.state ? `· ${r.state}` : ''}{' '}
                            {r.pin_code ? `· ${r.pin_code}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">
                          <div>{fmt(r.contact_number)}</div>
                          <div className="text-xs text-gray-500">{fmt(r.alternate_number)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">{fmt(r.status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">
                          <div className="font-medium">{fmt(r.disposition)}</div>
                          <div className="text-xs text-gray-500">{fmt(r.disposition_notes)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm align-top">
                          {isFetchingCalls && callCount === 0 ? (
                            <span className="text-xs text-gray-400">Loading...</span>
                          ) : callCount === 0 ? (
                            <span className="text-xs text-gray-400">No calls</span>
                          ) : (
                            <div className="flex items-center gap-1.5 text-xs">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                                <PhoneIncoming size={12} /> {callCount}
                              </span>
                              {recCount > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                                  <Mic size={12} /> {recCount}
                                </span>
                              ) : (
                                <span className="text-[11px] text-gray-400">no recording</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">{fmt(r.batch_name)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">{fmtDateTime(r.claimed_at)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">{fmtDateTime(r.disposition_updated_at)}</td>
                      </tr>
                      {isExpanded && callCount > 0 && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={9} className="px-6 py-4">
                            <div className="space-y-3">
                              {calls.map((c) => {
                                const recordings = extractRecordingUrls(c);
                                const provider = detectProvider(c);
                                const pb = providerBadge(provider);
                                const sb = callStatusBadge(c.call_status, c.call_disposition);
                                const dur =
                                  c.talk_duration_seconds ||
                                  c.agent_on_call_duration_seconds ||
                                  c.ivr_duration_seconds ||
                                  0;
                                return (
                                  <div
                                    key={String(c.id)}
                                    className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                                  >
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${pb.cls}`}
                                      >
                                        {pb.label}
                                      </span>
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sb.cls}`}
                                      >
                                        {sb.label}
                                      </span>
                                      {c.c_type ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                          {c.c_type}
                                        </span>
                                      ) : null}
                                      <span className="text-xs text-gray-600">
                                        <Clock size={12} className="inline -mt-0.5 mr-1" />
                                        {fmtDateTime(c.last_hangup_time || c.first_answer_time || c.received_at)}
                                      </span>
                                      <span className="text-xs text-gray-600">
                                        Talk: <strong className="text-gray-900">{fmtDuration(dur)}</strong>
                                      </span>
                                      {c.call_disposition ? (
                                        <span className="text-xs text-gray-600">
                                          Disposition:{' '}
                                          <strong className="text-gray-900">{c.call_disposition}</strong>
                                        </span>
                                      ) : null}
                                      <span className="text-[11px] text-gray-400 ml-auto">
                                        Call ID: {c.sarv_call_id}
                                      </span>
                                    </div>

                                    {recordings.length === 0 ? (
                                      <div className="text-xs text-gray-500 italic">
                                        No recording available for this call.
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {recordings.map((rec, idx) => (
                                          <div
                                            key={`${c.id}-${idx}`}
                                            className="flex flex-wrap items-center gap-3 bg-gray-50 rounded-md p-2"
                                          >
                                            <Mic size={14} className="text-emerald-600 flex-shrink-0" />
                                            <span className="text-xs font-medium text-gray-700 min-w-[80px]">
                                              {rec.label}
                                            </span>
                                            <audio
                                              controls
                                              preload="none"
                                              src={rec.url}
                                              className="flex-1 min-w-[260px] h-8"
                                            />
                                            <a
                                              href={rec.url}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                            >
                                              <ExternalLink size={12} /> Open
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WFHTelecallerDetailReport;

